import { useState, useEffect } from "react";
import { CheckCircle2, Send, Phone, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Smart inquiry form that adapts based on area availability status.
 * - "verfuegbar" → Standard inquiry form
 * - "warteliste" → Waitlist form with Fensterreinigung offer after submission
 */
export default function SmartAnfrageForm({ stadtName, stadtSlug, testIdPrefix, showHeadline = false }) {
  const [gebietStatus, setGebietStatus] = useState(null); // null = loading
  const [form, setForm] = useState({ name: "", email: "", phone: "", nachricht: "", stadt: stadtName, pflegegrad: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [anfrageId, setAnfrageId] = useState(null);
  const [showFenster, setShowFenster] = useState(false);
  const [fensterDone, setFensterDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/gebiete`).then(res => {
      const g = res.data.find(g => g.slug === stadtSlug);
      setGebietStatus(g && g.status !== "inaktiv" ? g.status : "verfuegbar");
    }).catch(() => setGebietStatus("verfuegbar"));
  }, [stadtSlug]);

  const isWarteliste = gebietStatus === "warteliste";
  const isLoading = gebietStatus === null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Bitte Name und Telefonnummer angeben."); return; }
    setSending(true);
    try {
      const payload = {
        ...form,
        quelle: isWarteliste ? "website-warteliste" : "website",
        kapazitaetsstatus: isWarteliste ? "warteliste" : "verfuegbar",
        anfragetyp: "alltagsbegleitung",
      };
      const res = await axios.post(`${API}/api/alltagsbegleitung/anfrage`, payload);
      setAnfrageId(res.data.id);
      if (isWarteliste) {
        setShowFenster(true);
      } else {
        setSent(true);
      }
      toast.success(isWarteliste ? "Sie wurden auf die Warteliste gesetzt!" : "Anfrage erfolgreich gesendet!");
    } catch { toast.error("Fehler beim Senden. Bitte rufen Sie uns an."); }
    setSending(false);
  };

  const handleFensterChoice = async (interesse) => {
    try {
      await axios.patch(`${API}/api/alltagsbegleitung/anfrage/${anfrageId}/fensterreinigung`, { interesse });
    } catch {
      toast.error("Fensterreinigung konnte nicht gespeichert werden.");
    }
    setFensterDone(true);
    setSent(true);
    setShowFenster(false);
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const headline = isWarteliste
    ? { title: `Warteliste für ${stadtName}`, sub: "Tragen Sie sich ein — wir melden uns, sobald ein Platz frei wird." }
    : { title: `Alltagsbegleitung in ${stadtName} anfragen`, sub: "Erzählen Sie uns kurz von Ihrer Situation. Wir melden uns innerhalb von 24 Stunden." };

  if (showFenster && !fensterDone) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid={`${testIdPrefix}-fenster-offer`}>
        <div className="text-center mb-6">
          <div className="h-14 w-14 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Sparkles className="h-7 w-7 text-orange-600" />
          </div>
          <h3 className="font-['Manrope'] text-xl font-bold text-slate-900 mb-2">Die Wartezeit sinnvoll überbrücken</h3>
          <p className="text-base text-slate-500 leading-relaxed">
            Während Sie auf einen regelmäßigen Platz in unserer Alltagsbegleitung warten, können wir Ihnen — sofern in Ihrer Region verfügbar — bereits unsere <strong>Fensterreinigung</strong> anbieten.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button onClick={() => handleFensterChoice(true)} data-testid={`${testIdPrefix}-fenster-ja`}
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white rounded-xl h-12 text-base font-semibold">
            Ja, ich interessiere mich dafür
          </Button>
          <Button variant="outline" onClick={() => handleFensterChoice(false)} data-testid={`${testIdPrefix}-fenster-nein`}
            className="flex-1 rounded-xl h-12 text-base">
            Nein, danke
          </Button>
        </div>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center" data-testid={`${testIdPrefix}-success`}>
        <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto mb-4" />
        <h3 className="font-['Manrope'] text-2xl font-bold text-slate-900 mb-2">
          {isWarteliste ? "Auf der Warteliste eingetragen!" : "Anfrage gesendet!"}
        </h3>
        <p className="text-base text-slate-600 mb-6">
          {isWarteliste
            ? "Vielen Dank! Wir melden uns bei Ihnen, sobald in Ihrer Region ein Platz frei wird."
            : "Vielen Dank! Wir melden uns innerhalb von 24 Stunden bei Ihnen."
          }
        </p>
        <a href="tel:+491637917044" className="inline-flex items-center gap-2 text-green-700 font-semibold hover:underline">
          <Phone className="h-4 w-4" /> Oder direkt anrufen: 0163 791 7044
        </a>
      </div>
    );
  }

  return (
    <>
      {showHeadline && (
        <div className="text-center mb-8">
          <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900 mb-3">{headline.title}</h2>
          <p className="text-base text-slate-500">{headline.sub}</p>
        </div>
      )}
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-5" data-testid={`${testIdPrefix}-anfrage-form`}>
      {isWarteliste && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-2">
          <p className="text-sm text-purple-800 font-medium">
            In {stadtName} sind aktuell alle Plätze belegt. Tragen Sie sich auf unsere Warteliste ein — wir melden uns, sobald ein Platz frei wird.
          </p>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Name *</Label>
          <Input data-testid={`${testIdPrefix}-name`} value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Ihr Name" className="h-12 rounded-xl" required />
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Telefon *</Label>
          <Input data-testid={`${testIdPrefix}-phone`} type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Ihre Telefonnummer" className="h-12 rounded-xl" required />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">E-Mail (optional)</Label>
          <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="ihre@email.de" className="h-12 rounded-xl" />
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Pflegegrad (optional)</Label>
          <select value={form.pflegegrad} onChange={e => setForm({...form, pflegegrad: e.target.value})} data-testid={`${testIdPrefix}-pflegegrad`}
            className="w-full h-12 rounded-xl border border-slate-200 px-4 text-sm">
            <option value="">— Bitte wählen —</option>
            <option value="1">Pflegegrad 1</option>
            <option value="2">Pflegegrad 2</option>
            <option value="3">Pflegegrad 3</option>
            <option value="4">Pflegegrad 4</option>
            <option value="5">Pflegegrad 5</option>
            <option value="beantragt">Beantragt</option>
            <option value="keiner">Kein Pflegegrad</option>
          </select>
        </div>
      </div>
      <div>
        <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Ihre Nachricht (optional)</Label>
        <textarea value={form.nachricht} onChange={e => setForm({...form, nachricht: e.target.value})}
          placeholder={isWarteliste ? "Haben Sie besondere Wünsche oder Anforderungen?" : `Wobei benötigen Sie Unterstützung in ${stadtName}?`}
          className="w-full h-28 rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-green-400 resize-none" />
      </div>
      <Button type="submit" disabled={sending} data-testid={`${testIdPrefix}-submit-btn`}
        className={`w-full rounded-xl h-14 text-lg font-semibold shadow-md transition-all ${isWarteliste ? "bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white" : "bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white"}`}>
        {sending ? "Wird gesendet..." : isWarteliste ? <><Send className="h-5 w-5 mr-2" /> Auf Warteliste eintragen</> : <><Send className="h-5 w-5 mr-2" /> Unverbindlich anfragen</>}
      </Button>
    </form>
    </>
  );
}
